import { useUserStore } from '@/common/stores/userStore';
import { ApiError, ApiResponse, RequestConfig } from './types';

export type TokenRefreshFn = (forceRefresh?: boolean) => Promise<string | null>;

export class ApiClientBase {
  protected baseURL: string;

  private static tokenRefreshFn: TokenRefreshFn | null = null;
  private static refreshPromise: Promise<string | null> | null = null;

  protected constructor(baseUrl: string) {
    this.baseURL = baseUrl || import.meta.env.VITE_API_BASE_URL;
  }

  /**
   * Configure the token refresh callback.
   * Called once at app initialization to wire authService.getAccessToken()
   * into all API clients without circular imports.
   */
  static setTokenRefreshFn(fn: TokenRefreshFn): void {
    ApiClientBase.tokenRefreshFn = fn;
  }

  /**
   * Ensures a fresh token is available.
   * Deduplicates concurrent calls so only one refresh is in-flight at a time.
   */
  private async ensureFreshToken(forceRefresh = false): Promise<string> {
    const userStore = useUserStore();

    if (ApiClientBase.tokenRefreshFn) {
      // Deduplicate: reuse in-flight refresh promise if not forcing
      if (!ApiClientBase.refreshPromise || forceRefresh) {
        ApiClientBase.refreshPromise = ApiClientBase.tokenRefreshFn(forceRefresh);
      }

      try {
        const token = await ApiClientBase.refreshPromise;
        if (!token) {
          throw new Error('Token refresh returned null. Session may have expired.');
        }
        userStore.setAccessToken(token);
        return token;
      } finally {
        ApiClientBase.refreshPromise = null;
      }
    }

    // Fallback: no refresh function configured, use store token directly
    const token = userStore.accessToken;
    if (!token) {
      throw new Error('No access token available. Please authenticate first.');
    }
    return token;
  }

  private buildUrl(endpoint: string): string {
    // Remove leading slash from endpoint if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  private parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    return (async () => {
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      if (contentLength === '0' || response.status === 204) {
        return { data: null as T, status: response.status };
      }

      const text = await response.text();
      if (!text.trim()) {
        return { data: null as T, status: response.status };
      }

      if (contentType?.includes('application/json')) {
        try {
          const data = JSON.parse(text);
          return { data: data as T, status: response.status };
        } catch {
          // JSON parse failed, fall through to return raw text
        }
      }

      return { data: text as T, status: response.status };
    })();
  }

  protected async request<T = unknown>(
    endpoint: string,
    config: RequestConfig
  ): Promise<ApiResponse<T>> {
    const userStore = useUserStore();
    const token = await this.ensureFreshToken();

    const url = this.buildUrl(endpoint);
    const currentLocale = userStore.culture || 'fr';
    // Convert to BCP 47 format for Accept-Language header
    const localeMap: Record<string, string> = {
      fr: 'fr-FR',
      en: 'en-US',
    };
    const acceptLanguage = localeMap[currentLocale.toLowerCase()] || 'fr-FR';

    const headers = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Accept-Language': acceptLanguage,
      ...config.headers,
    });

    let body: string | FormData | undefined;
    if (config.body) {
      if (config.body instanceof FormData) {
        headers.delete('Content-Type');
        body = config.body;
      } else {
        body = JSON.stringify(config.body);
      }
    }

    try {
      const response = await fetch(url, {
        method: config.method,
        headers,
        body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        const apiError: ApiError = {
          message: errorText || `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        };
        throw apiError;
      }

      return this.parseResponse<T>(response);
    } catch (error) {
      // On 401, attempt a forced token refresh and retry once
      if ((error as ApiError)?.status === 401 && ApiClientBase.tokenRefreshFn) {
        const freshToken = await this.ensureFreshToken(true);
        const retryHeaders = new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${freshToken}`,
          'Accept-Language': acceptLanguage,
          ...config.headers,
        });
        if (config.body instanceof FormData) {
          retryHeaders.delete('Content-Type');
        }

        const retryResponse = await fetch(url, {
          method: config.method,
          headers: retryHeaders,
          body,
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          const apiError: ApiError = {
            message: errorText || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            status: retryResponse.status,
          };
          throw apiError;
        }

        return this.parseResponse<T>(retryResponse);
      }

      if (error instanceof Error && !(error as any).status) {
        const apiError: ApiError = {
          message: error.message,
          status: 500,
        };
        throw apiError;
      }
      throw error;
    }
  }

  async get<T = unknown>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  async post<T = unknown>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, headers });
  }

  async put<T = unknown>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers });
  }

  async patch<T = unknown>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers });
  }

  async delete<T = unknown>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }
}
